/**
 * 上传组件
 *
 * 选择文件
 * 显示文件列表
 * 显示上传结果（仅错误）
 * 错误结果可能是一行，也可能是多行。但都是在message字段
 * 可能通过<br>来换行，也可能通过\n换行
 * 上传按钮的文字可能不同
 * 有的带下载模板
 */
import Vue, { CreateElement, PropType, VNode } from 'vue'

export interface UploadFile {
  uid: string
  name: string
  status: '' | 'uploading' | 'done' | 'error' | 'removed'
  // 原生File对象
  originFileObj: File
  //
  response: unknown
}

interface ChangeEvent {
  file: UploadFile
  fileList: UploadFile[]
}

function getExt(filename: string) {
  return filename.split('.').pop()
}

export interface ErrorRender {
  (h: CreateElement, data: any): VNode
}

interface ErrorRenderType {
  type: string
  render: ErrorRender
}

export default Vue.extend({
  name: 'AheduUpload',
  model: {
    prop: 'files',
    event: 'change',
  },
  props: {
    files: {
      type: Array as PropType<UploadFile[]>,
    },
    /**
     * 是否允许大写后缀
     */
    allowUpperCase: {
      type: Boolean,
      default: false,
    },
    /**
     * 单个文件大小限制
     * 单位为 MB
     * 默认为0，表示不限制
     */
    maxFileSize: {
      type: Number,
      default: 0,
    },
    /**
     * 文件数量限制
     * 默认为0，表示不限制
     */
    maxFileCount: {
      type: Number,
      default: 0,
    },
    /**
     * 允许上传的文件后缀名
     * 例如：['png','jpg','xls']
     * 为空数组，表示所有文件
     */
    exts: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /**
     * 下载模板地址
     */
    templateUrl: String,
  },
  data() {
    return {
      fileList: [] as UploadFile[],
      message: {
        /**
         * type 有以下几种
         * error 表示系统的一些错误，比如文件验证不通过之类的
         */
        type: '',
        data: null as unknown,
      },
      messageRenders: [] as ErrorRenderType[],
    }
  },
  computed: {
    _accept(): string {
      if (this.exts.length === 0) return '*'
      return this.exts.map((e) => '.' + e).join(',')
    },
  },
  watch: {
    files() {
      this.fileList = this.files
    },
  },
  methods: {
    registerErrorRender(type: string, render: ErrorRender): void {
      this.messageRenders.push({
        type,
        render,
      })
    },
    clearMessage(): void {
      this.setMessage('', null)
    },
    /**
     * type = error msg必须是Error类型
     * @param type
     * @param msg
     */
    setMessage(type: string, msg: unknown): void {
      this.message.type = type
      this.message.data = msg

      this.$emit('error', type, msg)
    },
    _action() {
      return Promise.resolve('/')
    },
    /**
     * 上传之前的验证
     */
    validate(file: UploadFile): Promise<void> {
      return new Promise((resolve, reject) => {
        try {
          this.validateExt(file)
        } catch (err: any) {
          reject(err)
        }
        resolve()
      })
    },
    validateExt(file: UploadFile) {
      // 验证文件格式
      const ext = getExt(file.name)
      // 如果不在列表里
      if (!this.exts.some((e) => e === ext)) {
        throw new Error('所选文件格式错误，请重新选择！')
      }

      return true
    },
    emit(): void {
      this.$emit('change', this.fileList)
    },

    onChange({ file, fileList }: ChangeEvent): void {
      this.clearMessage()

      this.fileList = fileList
      this.emit()
    },
    onRemove(uid: string): void {
      this.clearMessage()
      this.fileList = this.fileList.filter((f) => f.uid !== uid)
      this.emit()
      this.$emit('removed')
    },
    /**
     * 先验证文件
     * 再放到列表
     * 再手动上传
     */
    beforeUpload(file: UploadFile): Promise<UploadFile> {
      this.clearMessage()
      return Promise.resolve()
        .then(() => {
          return this.validate(file).catch((err) => {
            console.log(1, err)
            this.setMessage('error', err)
            return Promise.reject()
          })
        })
        .then(() => {
          file.status = ''
          this.fileList.push(file)
          this.emit()
          return Promise.reject()
        })
    },
    /**
     * 手动触发选择文件
     */
    chooseFile(): void {
      console.log('chooseFile')
      const upload = this.$el.querySelector('.ant-upload')
      if (upload) {
        const input = upload.querySelector('input') as HTMLInputElement
        if (input) {
          input.click()
        }
      }
    },
    renderLayout(h: CreateElement): VNode {
      return h('div')
    },
    renderFileList(h: CreateElement): VNode {
      return h('file-list', {
        props: {
          files: this.files,
        },
        on: {
          delete: (uid) => {
            this.onRemove(uid)
          },
        },
      })
    },
    renderUpload(h: CreateElement): VNode {
      return h('a-upload', {
        ref: 'upload',
        props: {
          action: this._action,
          beforeUpload: this.beforeUpload,
          fileList: this.fileList,
          accept: this._accept,
          showUploadList: false,
        },
        on: {
          change: (e) => {
            this.onChange(e)
          },
        },
      })
    },
    renderMessageByType(h: CreateElement, type: string, data: unknown): VNode {
      let content = null as VNode | null

      if (type === 'error') {
        content = h('div', { class: 'text-danger' }, (data as Error).message)
      }

      const t = this.messageRenders.find((mr) => mr.type === type)
      if (t) {
        content = t.render(h, data)
      }

      return h('div', [content])
    },
    renderMessage(h: CreateElement): VNode | null {
      const { type, data } = this.message
      if (!type) return null

      return h(
        'div',
        {
          class: ['upload-message', 'upload-message-' + type],
        },
        [this.renderMessageByType(h, type, data)]
      )
    },
    renderDownloadTemplate() {
      return (
        <a target="_blank" href={this.templateUrl}>
          下载模板
        </a>
      )
    },
  },
  render(h): VNode {
    return h(
      'div',
      {
        class: 'ahedu-upload',
      },
      [this.renderUpload(h), this.renderLayout(h)]
    )
  },
})
